import { z } from "zod";
import { PostMediaType, PostStatus } from "../enum.js";

export const createPostMediaSchema = z
	.object({
		url: z.url({ message: "Invalid media URL" }),
		type: z.enum(PostMediaType),
		thumbnail_url: z.url({ message: "Invalid thumbnail URL" }).nullish(),
		position: z.number().int().min(0),
		mime_type: z.string().max(255).nullish(),
		file_name: z.string().max(255).nullish(),
		file_size: z.number().int().nonnegative().nullish(),
	})
	.strict();

export const createPollSchema = z
	.object({
		options: z
			.array(z.string().min(1, { message: "Option text is required" }).max(200, { message: "Option text must be at most 200 characters" }))
			.min(2, { message: "A poll must have at least 2 options" })
			.max(10, { message: "A poll can have at most 10 options" }),
	})
	.strict();

export const createPostSchema = z
	.object({
		content: z.string().min(1, { message: "Post content is required" }),
		status: z.enum(PostStatus).default("published"),
		group_ids: z.array(z.uuid()).optional(),
		media: z.array(createPostMediaSchema).max(10, { message: "A post can have at most 10 media items" }).optional(),
		poll: createPollSchema.optional(),
		scheduled_at: z.iso.datetime({ message: "Invalid schedule date" }).optional(),
		mentioned_user_ids: z.array(z.uuid()).max(50, { message: "A post can mention at most 50 users" }).optional(),
		send_email: z.boolean().optional(),
	})
	.strict()
	.refine((data) => !(data.media && data.media.length > 0 && data.poll), {
		message: "A post cannot have both media and a poll",
	})
	.refine((data) => !(data.status === "scheduled" && !data.scheduled_at), {
		message: "Scheduled posts must have a scheduled_at date",
	})
	.refine(
		(data) => {
			if (!data.media || data.media.length === 0) return true;
			const hasImages = data.media.some((m) => m.type === "image");
			const hasVideos = data.media.some((m) => m.type === "video");
			return !(hasImages && hasVideos);
		},
		{ message: "A post cannot have both images and videos" },
	)
	.refine(
		(data) => {
			if (!data.media) return true;
			const videos = data.media.filter((m) => m.type === "video");
			return videos.length <= 1;
		},
		{ message: "A post can have at most 1 video" },
	);

export type CreatePostPayload = z.infer<typeof createPostSchema>;
export type CreatePostMediaPayload = z.infer<typeof createPostMediaSchema>;
export type CreatePollPayload = z.infer<typeof createPollSchema>;
